import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const initialState = { name: "", email: "", message: "" };

const ContactForm = () => {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = "Please enter your name";
    if (!data.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = "Enter a valid email";
    if (!data.message.trim()) e.message = "Please add a short message";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setSubmitting(true);
    try {
      await api.post("/leads/contact", data);
      setData(initialState);
      toast.success("Message sent. We'll reply within one business day.");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Could not send. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (k) => (ev) =>
    setData((d) => ({ ...d, [k]: ev.target.value }));

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="flex flex-col gap-6"
      noValidate
    >
      <Field
        label="Your Name"
        error={errors.name}
        testid="contact-name-field"
      >
        <input
          type="text"
          value={data.name}
          onChange={onChange("name")}
          data-testid="contact-name-input"
          placeholder="Jane Doe"
          className="w-full bg-transparent border-b border-brand-border focus:border-brand-cta py-3 text-base text-brand-ink placeholder:text-brand-inkSoft/50 outline-none transition-colors"
        />
      </Field>

      <Field
        label="Email Address"
        error={errors.email}
        testid="contact-email-field"
      >
        <input
          type="email"
          value={data.email}
          onChange={onChange("email")}
          data-testid="contact-email-input"
          placeholder="you@example.com"
          className="w-full bg-transparent border-b border-brand-border focus:border-brand-cta py-3 text-base text-brand-ink placeholder:text-brand-inkSoft/50 outline-none transition-colors"
        />
      </Field>

      <Field
        label="Message"
        error={errors.message}
        testid="contact-message-field"
      >
        <textarea
          rows={4}
          value={data.message}
          onChange={onChange("message")}
          data-testid="contact-message-input"
          placeholder="Tell us a bit about what you're thinking..."
          className="w-full bg-transparent border-b border-brand-border focus:border-brand-cta py-3 text-base text-brand-ink placeholder:text-brand-inkSoft/50 outline-none transition-colors resize-none"
        />
      </Field>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="self-start inline-flex items-center gap-3 px-6 py-4 bg-brand-ink hover:bg-brand-cta text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending..." : "Send Message"}
        <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
      </button>
    </form>
  );
};

const Field = ({ label, error, children, testid }) => (
  <label data-testid={testid} className="flex flex-col gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
      {label}
    </span>
    {children}
    {error && (
      <span className="text-xs text-brand-cta mt-1" data-testid={`${testid}-error`}>
        {error}
      </span>
    )}
  </label>
);

export default ContactForm;
